/**
 * **节点图标**（24×24 viewBox 的 stroke 路径）—— 内核与内置类型用的那一套。
 *
 * ## 为什么要有图标
 *
 * 用户原话：
 *
 * > 关于节点类型，是不是可以给节点配置一些图标，不然**改了名字不能一眼看懂
 * > 这是什么节点**，加了图标就可以不用给节点设置默认名字添加类型了
 *
 * 节点标题是**用户**的信号（他会改成"收件人地址"这种名字），图标是**类型**的
 * 信号。两者互不覆盖：改完名字之后，一眼仍然看得出"这是个文本运算 /
 * 打印组件 / 常量"。
 *
 * ## 画法（三个硬约束，别破）
 *
 * ① **24×24 的 viewBox**，和窗口类型图标（`EditorMeta.icon`）同一套坐标 ——
 *    两处图标会挨在一起出现（节点库里就有），比例不一致看着很脏。
 * ② **只有 stroke，没有 fill**：渲染侧统一给 `fill: none; stroke: currentColor`。
 *    写死的 `fill` 会让图标在悬停变色时露出一块死色。
 * ③ 线宽 / 端点在 CSS 里定（`stroke-width: 1.8; stroke-linecap: round`），
 *    路径里**不要**写 `stroke-width`。
 *
 * ## 两级回退
 *
 * ```text
 *   类型自己的 icon（`NodeTypeDef.icon`）   ← 想特化就特化（常量族、JS…）
 *   分类的 icon（`CATEGORY_ICONS`）        ← 78 个内置类型里绝大多数走这条
 *   都没有                                ← header 回落成那个「!」
 * ```
 *
 * ⚠️ 分类图标放在**内核**这张表里，而不是让每个包各写一份：
 * `logic` / `text` / `number` 这些分类是**基础能力**（函数包、数据包都是
 * JSON 数据包，写不了常量），零散地写在 JSON 里就等于同一批图标有十份拷贝。
 * 域包想给自己的分类换个图标，在它自己的 `categories` 里声明即可（优先）。
 */

/**
 * 按**节点类型**取图标 —— 只有值得特化的才在这里。
 *
 * ⚠️ 键是**类型 id**，不是标题：标题是给人看的、还会被改。
 */
export const NODE_ICONS = {
  /* ── 常量族：光有"常量"这个分类图标的话，六种常量长得一模一样 ── */
  'const.text': '<path d="M6 5h12M12 5v14M9 19h6"/>',
  'const.number': '<path d="M7 4L5 20M15 4l-2 16M4 9h16M3 15h16"/>',
  'const.boolean': '<rect x="3" y="7.5" width="18" height="9" rx="4.5"/><circle cx="8" cy="12" r="2.6"/>',
  'const.color': '<path d="M12 3.5s6 6.8 6 10.6a6 6 0 0 1-12 0C6 10.3 12 3.5 12 3.5z"/>',
  'const.datetime': '<circle cx="12" cy="12" r="8"/><path d="M12 7.2v5l3.2 2"/>',
  'const.list':
    '<path d="M9 6h11M9 12h11M9 18h11"/><circle cx="4.6" cy="6" r="1.3"/><circle cx="4.6" cy="12" r="1.3"/><circle cx="4.6" cy="18" r="1.3"/>',

  /* ── 图自己的构造（工具分类） ── */
  'core.js': '<path d="M8.5 6L4 12l4.5 6"/><path d="M15.5 6L20 12l-4.5 6"/>',
  loop: '<path d="M4.5 12a7.5 7.5 0 0 1 12.8-5.3M19.5 12a7.5 7.5 0 0 1-12.8 5.3"/><path d="M17.6 3.2v3.6H14M6.4 20.8v-3.6H10"/>',
  'loop.start': '<path d="M4 12h11"/><path d="M12 8l4 4-4 4"/><circle cx="19.5" cy="12" r="1.6"/>',
  'loop.output': '<circle cx="4.5" cy="12" r="1.6"/><path d="M9 12h11"/><path d="M12 8l4 4-4 4"/>',
  group: '<rect x="3" y="3" width="18" height="18" rx="3.5"/><rect x="8" y="8" width="8" height="8" rx="2"/>',
  'group.input': '<path d="M4 12h11"/><path d="M12 8l4 4-4 4"/><circle cx="19.5" cy="12" r="1.6"/>',
  'group.output': '<circle cx="4.5" cy="12" r="1.6"/><path d="M9 12h11"/><path d="M12 8l4 4-4 4"/>',
  reroute: '<circle cx="12" cy="12" r="3"/><path d="M3 12h6M15 12h6"/>',
  /* 框 / 注释：编辑器的构造，节点上认得出它们是"版面东西"而不是"算东西" */
  frame: '<rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="M3 9.5h18"/>',
  note: '<path d="M5 4.5h14v9.5l-5 5.5H5z"/><path d="M14 19.5V14h5"/>',
  'debug.viewer':
    '<path d="M2.5 12S6.5 6.5 12 6.5 21.5 12 21.5 12 17.5 17.5 12 17.5 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="2.6"/>',

} as const

/*
 * ⚠️ **这里只放内核自己的节点类型**（`const.*` / `core.*` / `loop` / `group` /
 * `frame` / `note` / `reroute` / `debug.viewer`）。
 *
 * 域包的类型标识（`event.*` / `print.*` / `tool.*`…）**不许出现在内核文件里** ——
 * `verify:boundary` 有一条专门盯着这件事（"内核里不出现包的节点类型标识"）。
 * 域包自己的图标写在它自己的 `index.ts` 里（`domains/event/index.ts` 那两个
 * 事件图标就是这么放的）：那样"卸载这个包"才是真的干净。
 */

/**
 * **分类 id → 图标**（回退表，见文件头）。
 *
 * 键用的是内置的那批分类 id。包自己声明了 `icon` 就优先用它 ——
 * 这张表只负责"没写的时候别是个空白"。
 */
export const CATEGORY_ICONS: Record<string, string> = {
  /* 常量：一个"装着值的盒子" —— 具体是哪一种由 `NODE_ICONS` 特化 */
  value: '<rect x="4" y="4" width="16" height="16" rx="3.5"/><circle cx="12" cy="12" r="3"/>',
  /* 工具：一圈辐条（"干活的家伙"），和常量那种"静止的盒子"明显不同 */
  utility:
    '<circle cx="12" cy="12" r="3"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.2 2.2M16.2 16.2l2.2 2.2M18.4 5.6l-2.2 2.2M7.8 16.2l-2.2 2.2"/>',
  /* 逻辑：菱形判断框 */
  logic: '<path d="M12 3l8.5 9-8.5 9-8.5-9z"/>',
  boolean: '<rect x="3" y="7.5" width="18" height="9" rx="4.5"/><circle cx="15.5" cy="12" r="2.6"/>',
  text: '<path d="M6 5h12M12 5v14M9 19h6"/>',
  number: '<path d="M7 4L5 20M15 4l-2 16M4 9h16M3 15h16"/>',
  color: '<path d="M12 3.5s6 6.8 6 10.6a6 6 0 0 1-12 0C6 10.3 12 3.5 12 3.5z"/>',
  time: '<circle cx="12" cy="12" r="8"/><path d="M12 7.2v5l3.2 2"/>',
  list: '<path d="M9 6h11M9 12h11M9 18h11"/><circle cx="4.6" cy="6" r="1.3"/><circle cx="4.6" cy="12" r="1.3"/><circle cx="4.6" cy="18" r="1.3"/>',
  /* 转换：一进一出两条箭头（"换个形状"） */
  convert: '<path d="M4 9h13l-3.2-3.2M20 15H7l3.2 3.2"/>',
  /* 数据：一只罐子（数据库那张经典图） */
  data: '<ellipse cx="12" cy="6.5" rx="7" ry="3"/><path d="M5 6.5v11c0 1.7 3.1 3 7 3s7-1.3 7-3v-11"/>',
  /* 事件：闪电（"这是个起点，它让图跑起来"） */
  event: '<path d="M13.5 3L6.5 13.5H12l-1.5 7.5 7-11H12z"/>',
  /* 网页：地球 */
  tool: '<circle cx="12" cy="12" r="8"/><path d="M4 12h16"/><path d="M12 4c2.6 3.2 2.6 12.8 0 16M12 4c-2.6 3.2-2.6 12.8 0 16"/>',
  /* 热敏打印：打印机（打印包自己也声明了一份，这里只是兜底） */
  print: '<path d="M7 9.5V4h10v5.5"/><rect x="4" y="9.5" width="16" height="7" rx="2"/><path d="M7 14.5h10V20H7z"/>',
}
